import Control from '../../1. Core/Control';
import DataTable from '../../1. Core/Controls/DataTable/DataTable';
import TopAppBar from '../../1. Core/Controls/TopAppBar/TopAppBar';
import Delegate from '../../1. Core/Delegate';
import '../FantasyList.scss';
import NP from '../../1. Core/NP';
import {ServerAPI} from '../../1. Core/ServerAPI';
import IconToggleButton from '../../1. Core/Controls/IconToggleButton/IconToggleButton';
import EditSpecialBundleDialog, {SpecialBundle} from './EditSpecialBundleDialog';
import Menu from '../../1. Core/Controls/Menu/Menu';
import ListItem from '../../1. Core/Controls/ListBox/ListItem';

let moment = require('moment');

interface SpecialBundleRow extends SpecialBundle {
	rewardsSummary: string;
	activeLabel: string;
	intervalLabel: string;
}

export default class SpecialBundleList extends Control {
	private _topAppBar: TopAppBar;
	private EvShowPageModeClick: Delegate;
	private _dataTable: DataTable;

	constructor(settings: object) {
		super(settings);
		this._render();
	}

	_initFields(settings: object) {
		this.EvShowPageModeClick = new Delegate();
		super._initFields(settings);
	}

	getTopAppBar() {
		return this._topAppBar;
	}

	_render() {
		this.np_render();
		this.addClass('');

		this._topAppBar = new TopAppBar({
			Class: 'top-app-bar--no-shadow top-app-bar--light library-box-list__app-bar',
			Caption: 'Special bundles',
			ParentNode: this._root,
			ShowSearchButton: false,
			MainButton: {
				FontImage: NP.FontImages.menu,
				EvClick: () => this.EvShowPageModeClick.fire(this),
			},
			RightItems: NP.getCurrentUser().isBDAdmin()
				? [
					new IconToggleButton({
						FontImage: NP.FontImages.add,
						ToolTip: 'Add bundle',
						EvClick: () =>
							new EditSpecialBundleDialog({
								EvOkButtonClick: () => this.refresh(),
							}).show(),
					}),
				]
				: [],
		});

		this._dataTable = new DataTable({
			ParentNode: this._root,
			ShowSettingsButton: NP.getCurrentUser().isBDAdmin(),
			Class: 'app-list--avatars mdc-data-table--margin data-table-battle',
			MultiSelect: false,
			ShowRowIndex: false,
			EnableSort: false,
			Columns: [
				{Content: 'Id', PropertyContent: 'id', Width: '8%'},
				{Content: 'Rewards', PropertyContent: 'rewardsSummary', Width: '34%'},
				{Content: 'Order', PropertyContent: 'display_order', Width: '8%'},
				{Content: 'Interval', PropertyContent: 'intervalLabel', Width: '28%'},
				{Content: 'Active', PropertyContent: 'activeLabel', Width: '8%'},
			],
			ShowPaginator: false,
			EvItemDoubleClick: (s: any, e: {Item: SpecialBundleRow}) => {
				if (NP.getCurrentUser().isBDAdmin()) {
					this.openEdit(e.Item);
				}
			},
			EvItemContextMenu: async (s: any, e: {Event: any; Item: SpecialBundleRow}) => {
				if (!NP.getCurrentUser().isBDAdmin()) {
					return;
				}
				const row = e.Item;
				const menu = new Menu({
					Items: [
						{FontImage: NP.FontImages.edit, Content: 'Edit', Value: 'edit'},
						{FontImage: NP.FontImages.delete, Content: 'Delete', Value: 'delete'},
					],
					EvItemSelected: async (sender: Menu, event: {Item: ListItem}) => {
						if (event.Item.getValue() === 'edit') {
							this.openEdit(row);
						} else if (event.Item.getValue() === 'delete') {
							if (confirm(`Delete bundle #${row.id}?`)) {
								await ServerAPI.delet(`/shop/special-bundles/${row.id}`);
								this.refresh();
							}
						}
					},
				});
				menu.showContextMenu(e.Event);
			},
		});

		this.refresh();
	}

	openEdit(row: SpecialBundle) {
		new EditSpecialBundleDialog({
			Bundle: row,
			EvOkButtonClick: () => this.refresh(),
		}).show();
	}

	buildRewardsSummary(bundle: SpecialBundle): string {
		const r = bundle.rewards || {coins: 0, diamonds: 0};
		const parts: string[] = [`${r.coins} coins`, `${r.diamonds} diamonds`];
		if (r.boosterPack) parts.push(`pack:${r.boosterPack}`);
		if (r.chestId) parts.push(`chest:${r.chestId}`);
		if (r.avatarCatalogId) parts.push(`avatarItem:#${r.avatarCatalogId}`);
		else if (r.avatarItemLevel) parts.push(`avatar:${r.avatarCategoryKey || '?'}/${r.avatarItemLevel}`);
		if (r.fighterCardCatalogIds?.length) parts.push(`fc:#${r.fighterCardCatalogIds.join(',')}`);
		return parts.join(', ');
	}

	buildIntervalLabel(bundle: SpecialBundle): string {
		const from = bundle.starts_at ? moment(bundle.starts_at).format('DD.MM.YYYY HH:mm') : '—';
		const to = bundle.ends_at ? moment(bundle.ends_at).format('DD.MM.YYYY HH:mm') : '—';
		return `${from} → ${to}`;
	}

	async refresh() {
		const rows = await ServerAPI.get<SpecialBundle[]>('/shop/special-bundles');
		this._dataTable.setRows(
			(rows || []).map(b => ({
				...b,
				rewardsSummary: this.buildRewardsSummary(b),
				activeLabel: b.active ? 'yes' : 'no',
				intervalLabel: this.buildIntervalLabel(b),
			})),
		);
	}
}
